import { SVGProps } from 'react'

/**
 * Stroke icons used by the flow (lucide geometry, the same paths as the Figma
 * frames). `currentColor`, so colour comes from the text class.
 */
const PATHS = {
   zap: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
   chat: (
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
   ),
   cal: (
      <>
         <rect x="3" y="4" width="18" height="18" rx="2" />
         <path d="M16 2v4M8 2v4M3 10h18" />
      </>
   ),
   phone: (
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.8 2z" />
   ),
   users: (
      <>
         <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
         <circle cx="9" cy="7" r="4" />
         <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
      </>
   ),
   home: <path d="m3 11 9-8 9 8v9a2 2 0 0 1-2 2h-4v-6H9v6H5a2 2 0 0 1-2-2z" />,
   check: <path d="M20 6 9 17l-5-5" />,
   chev: <path d="m9 18 6-6-6-6" />,
   chevDown: <path d="m6 9 6 6 6-6" />,
   back: <path d="m15 18-6-6 6-6" />,
   play: <path d="m6 4 14 8-14 8z" fill="currentColor" />,
   pause: <path d="M8 5v14M16 5v14" />,
   plus: <path d="M12 5v14M5 12h14" />,
   pen: (
      <>
         <path d="M12 20h9" />
         <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
      </>
   ),
   search: (
      <>
         <circle cx="11" cy="11" r="7" />
         <path d="m21 21-4.3-4.3" />
      </>
   ),
   spark: (
      <path d="m12 3 1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
   ),
   info: (
      <>
         <circle cx="12" cy="12" r="9" />
         <path d="M12 16v-4M12 8h.01" />
      </>
   ),
   vm: (
      <>
         <circle cx="6" cy="12" r="3" />
         <circle cx="18" cy="12" r="3" />
         <path d="M6 15h12" />
      </>
   ),
   clock: (
      <>
         <circle cx="12" cy="12" r="9" />
         <path d="M12 7v5l3 2" />
      </>
   ),
   x: <path d="M18 6 6 18M6 6l12 12" />,
   eye: (
      <>
         <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
         <circle cx="12" cy="12" r="3" />
      </>
   ),
   arrowLeft: <path d="M19 12H5M12 19l-7-7 7-7" />,
   arrowRight: <path d="M5 12h14M12 5l7 7-7 7" />,
   chevs: <path d="m6 17 5-5-5-5M13 17l5-5-5-5" />,
   pin: (
      <>
         <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
         <circle cx="12" cy="10" r="3" />
      </>
   ),
   trend: (
      <>
         <path d="m22 7-8.5 8.5-5-5L2 17" />
         <path d="M16 7h6v6" />
      </>
   ),
   voice: <path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" />,
   send: (
      <>
         <path d="m22 2-7 20-4-9-9-4z" />
         <path d="M22 2 11 13" />
      </>
   ),
   refresh: (
      <>
         <path d="M21 12a9 9 0 1 1-3-6.7" />
         <path d="M21 3v6h-6" />
      </>
   ),
   drag: (
      <>
         <circle cx="9" cy="6" r="1.2" />
         <circle cx="15" cy="6" r="1.2" />
         <circle cx="9" cy="12" r="1.2" />
         <circle cx="15" cy="12" r="1.2" />
         <circle cx="9" cy="18" r="1.2" />
         <circle cx="15" cy="18" r="1.2" />
      </>
   ),
   trash: <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />,
   rocket: (
      <>
         <path d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2" />
         <path d="M9 12a12 12 0 0 1 11-9 12 12 0 0 1-9 11l-3 1-1-3z" />
      </>
   ),
} as const

export type TFlowIcon = keyof typeof PATHS

export const FlowIcon = ({
   name,
   size = 16,
   strokeWidth = 1.8,
   ...rest
}: {
   name: TFlowIcon
   size?: number
   strokeWidth?: number
} & SVGProps<SVGSVGElement>) => (
   <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
      {...rest}
   >
      {PATHS[name]}
   </svg>
)
